import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { contentAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';

const Yoga = () => {
  // Live Class Feature Enabled
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchYogaVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYogaVideos = async () => {
    try {
      const res = await contentAPI.getYogaSessions();
      if (res.data?.success) {
        setVideos(res.data.data);
      }
    } catch (error) {
      showError('Failed to fetch yoga videos');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'beginner', 'intermediate', 'advanced', 'meditation'];

  const filteredVideos = selectedCategory === 'all'
    ? videos
    : videos.filter(video => video.category === selectedCategory);

  const playVideo = (video) => {
    navigate(`/patient/live-class/${video._id}`);
  };

  if (loading) {
    return <div className="text-center py-8">Loading yoga content...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Yoga & Wellness</h1>
        <button className="bg-gold text-dark px-4 py-2 rounded-lg font-medium">
          Favorites
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex overflow-x-auto space-x-2 pb-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-full whitespace-nowrap ${selectedCategory === category
              ? 'bg-gold text-dark'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredVideos.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-400">No yoga videos found</p>
            <p className="text-sm text-gray-500 mt-2">Check back later for new content</p>
          </div>
        ) : (
          filteredVideos.map((video) => (
            <div key={video._id} className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="relative aspect-video bg-gray-700">
                <div className="absolute inset-0 flex items-center justify-center">
                  {user?.subscription_status === 'active' ? (
                    <button
                      onClick={() => playVideo(video)}
                      className="bg-gold text-dark rounded-full p-3 hover:scale-105 transition-transform"
                    >
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        showError('Please subscribe to watch yoga videos');
                        // Optional: Navigate to subscription page
                      }}
                      className="bg-gray-900/80 text-white rounded-full p-3 hover:bg-gray-900 transition-colors flex items-center gap-2 px-4"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-sm font-medium">Subscribe to Watch</span>
                    </button>
                  )}
                </div>
                {/* Thumbnail logic if needed, currently assumes video listing shows title/desc below */}
                {video.thumbnail_url && (
                  <img src={formatImageUrl(video.thumbnail_url)} alt={video.title} className="w-full h-full object-cover opacity-50 absolute inset-0 -z-10" />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-2">{video.title}</h3>
                <p className="text-gray-400 text-sm mb-3">{video.description}</p>
                <div className="flex justify-between items-center">
                  <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs">
                    {video.category}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {video.duration} min
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Player Modal */}

    </div>
  );
};

export default Yoga;
